/* Version: 3.5.149 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import 'react';
import PropTypes from 'prop-types';
import Stack from '../Stack/Stack.js';
import { safeRest, uniqueId } from '../../utils/functions.js';
import safeCustomStyle from '../../utils/safeCustomStyle.js';
import { ErrorMsg, ErrorIcon } from './styledComponents.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';
import useDerivedState from '../../hooks/useDerivedState.js';
import useId from '../../hooks/useId.js';

var _excluded = ["id", "errors", "callouts", "customStyle"];
var Errors = function Errors(_ref) {
  var controlledId = _ref.id,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$callouts = _ref.callouts,
    callouts = _ref$callouts === undefined ? false : _ref$callouts,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {
      rootStyle: {}
    } : _ref$customStyle,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var _useDerivedState = useDerivedState(useId(), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var safeProps = safeRest(rest);
  var _safeCustomStyle = safeCustomStyle(customStyle),
    rootStyle = _safeCustomStyle.rootStyle;
  var _ref2 = theme.errors || {},
    showIcon = _ref2.showIcon;
  function getErrorContent(err) {
    if (_typeof(err) === 'object' && typeof err.isError !== 'undefined' && err.isError) {
      return err.msg;
    }
    return err;
  }
  if (errors && errors.length) {
    return /*#__PURE__*/jsx(ErrorMsg, _objectSpread(_objectSpread({
      id: "".concat(id, "-errors"),
      "aria-atomic": callouts ? 'true' : null,
      "aria-live": callouts ? 'polite' : null,
      role: callouts ? 'alert' : 'none',
      "data-testid": "inline-errors-root",
      theme: theme,
      rootStyle: rootStyle
    }, safeProps), {}, {
      children: /*#__PURE__*/jsx(Stack, {
        align: "flex-start",
        gap: showIcon ? '0.5rem' : '0',
        children: errors.map(function (error) {
          var isErrorObject = _typeof(error) === 'object';
          var isErrorSet = isErrorObject && typeof error.isError !== 'undefined';
          var key = isErrorObject && error.id ? error.id : uniqueId("".concat(id, "-"));
          if (isErrorSet && !error.isError) {
            return null;
          }
          return showIcon ? /*#__PURE__*/jsxs(Stack, {
            direction: "row",
            align: "flex-start",
            gap: "0.5rem",
            wrap: "nowrap",
            children: [/*#__PURE__*/jsx(ErrorIcon, {
              "data-testid": "error-icon"
            }), /*#__PURE__*/jsx("span", {
              children: getErrorContent(error)
            })]
          }, key) : /*#__PURE__*/jsx("span", {
            children: getErrorContent(error)
          }, key);
        })
      })
    }));
  }
  return null;
};
process.env.NODE_ENV !== "production" ? Errors.propTypes = {
  /** DOM element ID for form control */
  id: PropTypes.string,
  /** Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen). */
  callouts: PropTypes.bool,
  /** Array of error messages */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    // error id
    msg: PropTypes.any,
    // localized error text
    isError: PropTypes.bool // error condition
  })), PropTypes.arrayOf(PropTypes.string)]),
  /** Overrides for styling */
  customStyle: PropTypes.shape({
    rootStyle: PropTypes.object
  })
} : undefined;
process.env.NODE_ENV !== "production" ? Errors.displayName = 'Errors' : undefined;

export { Errors as default };
